// The shared PDF / print layout used by every document Falcon generates (Tech Pack, PO, QC report, packing list,
// shipment and landed cost documents, Order timeline, finance reports, help pages). The content is laid out into real
// A4 pages before printing or saving, following the house rules:
//  - a figure block (title + drawing + caption, front / back pairs) is never split: it moves to the next page whole,
//    and one taller than a page is scaled to fit, with a scale note;
//  - table rows are never split; a table that continues repeats its header row, marked "(continued)";
//  - headings are never left alone at the bottom of a page;
//  - every page has the running header and footer ("Confidential – organisation", or just the organisation on a document
//    made for a customer; generated date / time and user)
//    and a correct "Page X of Y", counted after the layout;
//  - nothing goes past the margins: wide tables wrap and shrink (to a readable minimum), then move to a landscape
//    page; drawings scale to the printable area;
//  - sections flow one after another (optionally each main section on a new page); empty sections are one line;
//  - stray spaces around hyphens and runs of spaces are tidied.
// Source markup: any element. Its direct children are the flow. Inside, `.pdf-section` groups are flattened,
// `.pdf-grid` containers keep their grid (items move page by page), `.pdf-keep` / `figure` blocks stay together,
// `table` splits by rows, `h1`–`h4` / `.pdf-heading` keep with the next block, `.pdf-page-break` starts a new page,
// `.pdf-first` appears on page 1 only (e.g. the organisation block). Elements with `data-actual-mm="W×H"` get a scale note.
import html2canvas from "html2canvas";
import { jpegPdf } from "./pdfzip.js";
import { download, inlineImages } from "./labelpdf.js";
import { formatDateTime } from "./lib.js";
import { state } from "./state.js";

export interface PdfOptions {
  /** Running header, left (e.g. "ARC002-PO · Work shirt · Ocean Blue"). */
  title: string;
  /** Running header, right (e.g. "Rev 0 · Draft"). */
  subtitle?: string;
  /** Each main section (`.pdf-section`) on a new page (default off: sections flow). */
  newPagePerSection?: boolean;
  /** The whole document in landscape. */
  landscape?: boolean;
  /** Organisation and user for the footer (default: the signed-in session). */
  organisation?: string;
  user?: string;
  /** An extra footer line on every page (e.g. "Contains confidential composition" for a trade-secret fabric's full composition). */
  footerNote?: string;
  /** In place of "Confidential – organisation" on documents made for customers (a quote, an order confirmation, a price list). */
  footerLeft?: string;
}

const MM = 96 / 25.4; // CSS px per mm
const MIN_SHRINK = 3; // table shrink steps: 90%, 80%, 70% (7 pt minimum on a 10 pt base)

/** Tidy generated text: no stray space before a hyphen joining a code ("ARC002 -PO"), single spaces. */
export function tidyText(s: string): string {
  return s.replace(/[  ]{2,}/g, " ").replace(/(\w) -(\w)/g, "$1-$2").replace(/(\w)- (\w)(?=\w*\d)/g, "$1-$2").replace(/ ,/g, ",");
}
function tidyNodes(root: Node) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.nodeValue && /\S/.test(n.nodeValue)) n.nodeValue = tidyText(n.nodeValue);
}

interface Page { el: HTMLElement; body: HTMLElement; landscape: boolean }
interface Flow { el: HTMLElement; grid: HTMLElement | null; sectionStart: boolean }

function flatten(src: HTMLElement, opts: PdfOptions): Flow[] {
  const out: Flow[] = [];
  const walk = (parent: HTMLElement, grid: HTMLElement | null, sectionStart: boolean) => {
    let first = sectionStart;
    for (const child of Array.from(parent.children) as HTMLElement[]) {
      if (child.classList.contains("pdf-section")) { walk(child, null, !!opts.newPagePerSection); first = false; continue; }
      // Plain wrappers flow their children (so a long table inside them can continue on the next page).
      if (!child.classList.contains("pdf-keep") && (child.classList.contains("pdf-flow") || child.classList.contains("tpd-page") || child.classList.contains("table-wrap"))) { walk(child, grid, first); first = false; continue; }
      if (child.classList.contains("pdf-grid")) {
        const shell = child.cloneNode(false) as HTMLElement;
        for (const item of Array.from(child.children) as HTMLElement[]) { out.push({ el: item, grid: shell, sectionStart: first }); first = false; }
        continue;
      }
      out.push({ el: child, grid, sectionStart: first });
      first = false;
    }
  };
  walk(src, null, false);
  return out;
}

const isHeading = (el: HTMLElement) => /^H[1-4]$/.test(el.tagName) || el.classList.contains("pdf-heading");
const isTable = (el: HTMLElement) => el.tagName === "TABLE" && !!el.querySelector("tbody tr");
const overflowsY = (body: HTMLElement) => body.scrollHeight > body.clientHeight + 1;
function overflowsX(el: HTMLElement, body: HTMLElement): boolean {
  const b = body.getBoundingClientRect();
  if (el.scrollWidth > el.clientWidth + 1 && el.clientWidth > 0) return true;
  const r = el.getBoundingClientRect();
  if (r.right > b.right + 1 || r.left < b.left - 1) return true;
  for (const d of Array.from(el.querySelectorAll<HTMLElement>("table, svg, img, pre"))) {
    const x = d.getBoundingClientRect();
    if (x.width && (x.right > b.right + 1 || x.left < b.left - 1)) return true;
  }
  return false;
}

/** Lay the source out into pages. Returns the element holding the pages (already in the document, off-screen unless
 *  `into` is given). */
export async function layoutDocument(source: HTMLElement, opts: PdfOptions, into?: HTMLElement): Promise<HTMLElement> {
  const host = document.createElement("div");
  host.className = "pdf-doc";
  if (into) into.replaceChildren(host);
  else { host.classList.add("pdf-offscreen"); document.body.appendChild(host); }
  const src = source.cloneNode(true) as HTMLElement;
  tidyNodes(src);
  // Wait for images so heights are right.
  await Promise.all(Array.from(src.querySelectorAll("img")).map((im) => (im.complete ? null : new Promise((r) => { im.addEventListener("load", r, { once: true }); im.addEventListener("error", r, { once: true }); }))));
  const org = opts.organisation ?? (state.session.organisation?.name || "");
  const user = opts.user ?? [state.session.user?.firstName, state.session.user?.lastName].filter(Boolean).join(" ");
  const stamp = formatDateTime(new Date().toISOString());
  const pages: Page[] = [];
  const newPage = (landscape = !!opts.landscape): Page => {
    const el = document.createElement("section");
    el.className = `pdf-page${landscape ? " pdf-landscape" : ""}`;
    el.innerHTML = `<header class="pdf-head"><span class="pdf-head-title"></span><span class="pdf-head-sub"></span></header><div class="pdf-body"></div>
<footer class="pdf-foot"><span>${escapeHtml(opts.footerLeft ?? `Confidential – ${org}`)}${opts.footerNote ? ` · <strong class="pdf-foot-note">${escapeHtml(opts.footerNote)}</strong>` : ""}</span><span>Generated ${escapeHtml(stamp)}${user ? ` by ${escapeHtml(user)}` : ""}</span><span class="pdf-pageno"></span></footer>`;
    el.querySelector(".pdf-head-title")!.textContent = opts.title;
    el.querySelector(".pdf-head-sub")!.textContent = opts.subtitle || "";
    host.appendChild(el);
    const body = el.querySelector<HTMLElement>(".pdf-body")!;
    // The source's own classes, so its styles (e.g. `.tp-document table`) still apply inside the page.
    for (const c of Array.from(source.classList)) if (c !== "pdf-source") body.classList.add(c);
    const p = { el, body, landscape };
    pages.push(p);
    return p;
  };
  let page = newPage();
  const firstOnly = Array.from(src.querySelectorAll<HTMLElement>(":scope > .pdf-first"));
  for (const f of firstOnly) { page.body.appendChild(f); }
  const flows = flatten(src, opts).filter((f) => !f.el.classList.contains("pdf-first"));
  let gridShell: HTMLElement | null = null, gridClone: HTMLElement | null = null;
  const isEmptyPage = (p: Page) => !p.body.children.length || Array.from(p.body.children).every((c) => c.classList.contains("pdf-first"));
  /** Move trailing headings of the current page to the new page (keep with next). */
  const breakPage = (landscape = !!opts.landscape): Page => {
    const trailing: HTMLElement[] = [];
    let last = page.body.lastElementChild as HTMLElement | null;
    while (last && isHeading(last) && page.body.children.length > trailing.length + 1) { trailing.unshift(last); last = last.previousElementSibling as HTMLElement | null; }
    page = newPage(landscape);
    for (const h of trailing) page.body.appendChild(h);
    gridClone = null;
    return page;
  };
  const target = (f: Flow): HTMLElement => {
    if (!f.grid) { gridShell = null; gridClone = null; return page.body; }
    if (gridShell !== f.grid || !gridClone || gridClone.parentElement !== page.body) {
      gridShell = f.grid;
      gridClone = f.grid.cloneNode(false) as HTMLElement;
      page.body.appendChild(gridClone);
    }
    return gridClone;
  };
  /** A table with more columns than fit at a readable width (about 11 mm each) is cramped even if it wraps. */
  const cramped = (el: HTMLElement): boolean => {
    const t = el.tagName === "TABLE" ? el : el.querySelector("table");
    if (!t) return false;
    const cols = Math.max(0, ...Array.from(t.querySelectorAll("tr")).slice(0, 5).map((r) => Array.from(r.children).reduce((n, c) => n + ((c as HTMLTableCellElement).colSpan || 1), 0)));
    return cols * 11 * MM > page.body.clientWidth + 1;
  };
  const fitWidth = (el: HTMLElement): boolean => {
    if (cramped(el)) { el.classList.add("pdf-shrink-3"); return false; }
    if (!overflowsX(el, page.body)) return true;
    // Wrap and shrink step by step.
    for (let s = 1; s <= MIN_SHRINK; s++) {
      el.classList.remove(`pdf-shrink-${s - 1}`);
      el.classList.add(`pdf-shrink-${s}`);
      if (!overflowsX(el, page.body)) return true;
    }
    return false;
  };
  /** Scale a block that is taller or wider than a whole page. */
  const scaleToFit = (el: HTMLElement) => {
    const b = page.body.getBoundingClientRect(), r = el.getBoundingClientRect();
    // Room left below the block's top (it may not be alone on the page).
    const room = b.bottom - r.top - 2;
    const k = Math.min(1, room / Math.max(1, r.height), b.width / Math.max(1, r.width));
    if (k < 1) { const now = Number((el.style as any).zoom || 1); (el.style as any).zoom = String(Math.floor(now * k * 1000) / 1000); el.classList.add("pdf-scaled"); }
  };
  const placeTable = (tbl: HTMLElement, title: string) => {
    const rows = Array.from(tbl.querySelectorAll<HTMLTableRowElement>(":scope > tbody > tr"));
    const head = tbl.querySelector(":scope > thead");
    const foot = tbl.querySelector(":scope > tfoot");
    const shell = () => { const t = tbl.cloneNode(false) as HTMLElement; if (head) t.appendChild(head.cloneNode(true)); t.appendChild(document.createElement("tbody")); return t; };
    let t = shell(), cont = false;
    page.body.appendChild(t);
    const orient = page.landscape;
    const shrinkClass = Array.from(tbl.classList).find((c) => c.startsWith("pdf-shrink-"));
    for (const row of rows) {
      t.querySelector("tbody")!.appendChild(row);
      if (!overflowsY(page.body)) continue;
      row.remove();
      const onlyHead = !t.querySelector("tbody tr");
      if (onlyHead) t.remove();
      breakPage(orient);
      const note = document.createElement("div");
      note.className = "pdf-cont";
      note.textContent = `${title || "Table"} (continued)`;
      if (!onlyHead || cont) page.body.appendChild(note);
      t = shell(); if (shrinkClass) t.classList.add(shrinkClass);
      page.body.appendChild(t);
      t.querySelector("tbody")!.appendChild(row);
      cont = true;
      if (overflowsY(page.body)) scaleToFit(row); // a single row taller than a page
    }
    // The totals row goes at the end (with the last rows).
    if (foot) {
      t.appendChild(foot);
      if (overflowsY(page.body)) {
        foot.remove();
        const lastRow = t.querySelector("tbody tr:last-child");
        breakPage(orient);
        const note = document.createElement("div"); note.className = "pdf-cont"; note.textContent = `${title || "Table"} (continued)`;
        page.body.appendChild(note);
        t = shell(); if (shrinkClass) t.classList.add(shrinkClass);
        page.body.appendChild(t);
        if (lastRow) t.querySelector("tbody")!.appendChild(lastRow);
        t.appendChild(foot);
      }
    }
  };
  /** A block that stopped fitting after its scale note was added: move it on, or scale it a little more. */
  const settle = (el: HTMLElement, f: Flow) => {
    const alone = page.body.children.length === 1 || (el.parentElement !== page.body && page.body.children.length === 1);
    if (alone) { scaleToFit(el); return; }
    el.remove();
    breakPage(page.landscape);
    target(f).appendChild(el);
    if (overflowsY(page.body)) scaleToFit(el);
  };
  let prevTitle = "";
  for (let i = 0; i < flows.length; i++) {
    const f = flows[i];
    const el = f.el;
    if (el.classList.contains("pdf-page-break")) { if (!isEmptyPage(page)) breakPage(); continue; }
    if (f.sectionStart && !isEmptyPage(page)) breakPage();
    if (isHeading(el)) prevTitle = (el.textContent || "").replace(/\s+/g, " ").trim();
    const wantLandscape = el.classList.contains("pdf-landscape-block");
    if (wantLandscape !== page.landscape && !opts.landscape && (wantLandscape || !isEmptyPage(page))) {
      if (!isEmptyPage(page) || wantLandscape) breakPage(wantLandscape);
    }
    const into = target(f);
    into.appendChild(el);
    // Width: wrap / shrink; still too wide → a landscape page (tables and wide blocks), or scale (drawings).
    if (!fitWidth(el)) {
      if (!page.landscape && (el.tagName === "TABLE" || el.querySelector("table"))) {
        el.remove();
        el.classList.add("pdf-landscape-block");
        breakPage(true);
        page.body.appendChild(el);
        fitWidth(el);
        if (overflowsX(el, page.body)) scaleToFit(el);
      } else scaleToFit(el);
    }
    if (!overflowsY(page.body)) { noteScale(el); if (overflowsY(page.body)) settle(el, f); continue; }
    // Doesn't fit in the space left.
    if (isTable(el) && !el.closest(".pdf-keep")) {
      el.remove();
      if (into !== page.body && !into.children.length) into.remove();
      placeTable(el, prevTitle);
      continue;
    }
    el.remove();
    if (into !== page.body && !into.children.length) into.remove();
    if (!isEmptyPage(page)) breakPage(page.landscape);
    const again = target(f);
    again.appendChild(el);
    if (overflowsY(page.body)) scaleToFit(el);
    noteScale(el);
    if (overflowsY(page.body)) scaleToFit(el);
  }
  // Leave no blank last page; then number the pages.
  const last = pages[pages.length - 1];
  if (pages.length > 1 && isEmptyPage(last)) { last.el.remove(); pages.pop(); }
  pages.forEach((p, i) => { p.el.querySelector(".pdf-pageno")!.textContent = `Page ${i + 1} of ${pages.length}`; });
  host.dataset.pages = String(pages.length);
  return host;
}

/** Scale notes for drawings with a known actual size (`data-actual-mm="300×400"`). */
function noteScale(el: HTMLElement) {
  const targets = el.matches("[data-actual-mm]") ? [el] : Array.from(el.querySelectorAll<HTMLElement>("[data-actual-mm]"));
  for (const t of targets) {
    const [w] = String(t.dataset.actualMm).split(/[×x]/).map(Number);
    const art = t.querySelector<HTMLElement>("svg, img") || t;
    const shown = art.getBoundingClientRect().width / MM;
    if (!w || !shown) continue;
    const pct = Math.round((shown / w) * 100);
    let note = t.querySelector<HTMLElement>(":scope > .pdf-scale-note");
    if (!note) { note = document.createElement("small"); note.className = "pdf-scale-note"; t.appendChild(note); }
    note.textContent = pct >= 98 && pct <= 102 ? "Actual size" : `Shown at ${pct}% of actual size`;
  }
}

function escapeHtml(s: string) { return s.replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[m]!); }

/** Save laid-out pages as a PDF: one image per page, each exactly A4 (portrait or landscape). */
export async function pagesToPdfBytes(host: HTMLElement, progress?: (s: string) => void): Promise<Uint8Array> {
  // Pictures go inside the pages (data URLs) so the browser can draw each page exactly as shown.
  const cache = new Map<string, string>();
  for (const img of Array.from(host.querySelectorAll("img"))) {
    const src = img.getAttribute("src") || "";
    if (!src || src.startsWith("data:")) continue;
    try {
      if (!cache.has(src)) {
        const blob = await (await fetch(src, { credentials: "same-origin" })).blob();
        cache.set(src, await new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = no; r.readAsDataURL(blob); }));
      }
      img.src = cache.get(src)!;
      await img.decode().catch(() => undefined);
    } catch { /* leave it */ }
  }
  for (const svg of Array.from(host.querySelectorAll<SVGSVGElement>("svg"))) {
    if (!/href="(?!data:|#)/.test(svg.outerHTML)) continue;
    const tmp = document.createElement("div");
    tmp.innerHTML = await inlineImages(svg.outerHTML);
    const fresh = tmp.firstElementChild;
    if (fresh) svg.replaceWith(fresh);
  }
  const pages = Array.from(host.querySelectorAll<HTMLElement>(":scope > .pdf-page"));
  const out: Array<{ jpeg: Uint8Array; width: number; height: number; landscape?: boolean }> = [];
  for (let i = 0; i < pages.length; i++) {
    progress?.(`Page ${i + 1} of ${pages.length}…`);
    const p = pages[i];
    let canvas: HTMLCanvasElement;
    // The browser draws the page itself (exact text spacing); the older drawing is the fallback.
    // The pages are laid out off screen; the browser's own drawing must be told where the page is, or it draws the page
    // outside the picture and leaves it empty.
    const at = p.getBoundingClientRect();
    try { canvas = await html2canvas(p, { scale: 2, backgroundColor: "#ffffff", logging: false, useCORS: true, foreignObjectRendering: true, x: -at.left, y: -at.top }); if (!canvas.width) throw new Error("empty"); }
    catch { canvas = await html2canvas(p, { scale: 2, backgroundColor: "#ffffff", logging: false, useCORS: true }); }
    const blob = await new Promise<Blob>((r, x) => canvas.toBlob((b) => (b ? r(b) : x(new Error("The page couldn't be drawn."))), "image/jpeg", 0.9));
    out.push({ jpeg: new Uint8Array(await blob.arrayBuffer()), width: canvas.width, height: canvas.height, landscape: p.classList.contains("pdf-landscape") });
  }
  return jpegPdf(out);
}

/** Lay out and download as a PDF. */
export async function documentToPdf(source: HTMLElement, opts: PdfOptions, fileName: string, progress?: (s: string) => void): Promise<void> {
  const host = await layoutDocument(source, opts);
  try { download(fileName, await pagesToPdfBytes(host, progress), "application/pdf"); } finally { host.remove(); }
}
/** The same bytes (for emailing). */
export async function documentPdfBytes(source: HTMLElement, opts: PdfOptions, progress?: (s: string) => void): Promise<Uint8Array> {
  const host = await layoutDocument(source, opts);
  try { return await pagesToPdfBytes(host, progress); } finally { host.remove(); }
}

/** Lay out and print (each laid-out page is one printed page, with its header, footer and page number). */
export async function printDocument(source: HTMLElement, opts: PdfOptions, docTitle?: string): Promise<void> {
  const host = await layoutDocument(source, opts);
  host.classList.remove("pdf-offscreen");
  host.classList.add("pdf-printing");
  document.body.classList.add("pdf-print-mode");
  const before = document.title;
  if (docTitle) document.title = docTitle;
  const done = () => { host.remove(); document.body.classList.remove("pdf-print-mode"); document.title = before; window.removeEventListener("afterprint", done); };
  window.addEventListener("afterprint", done);
  window.print();
  setTimeout(() => { if (host.isConnected && !window.matchMedia("print").matches) done(); }, 1500);
}

/** A check used by the automated test and in development: every rule the layout must keep. */
export function checkLayout(host: HTMLElement): string[] {
  const problems: string[] = [];
  const pages = Array.from(host.querySelectorAll<HTMLElement>(":scope > .pdf-page"));
  const seen = new Map<string, number>();
  pages.forEach((p, i) => {
    const no = p.querySelector(".pdf-pageno")?.textContent || "";
    if (no !== `Page ${i + 1} of ${pages.length}`) problems.push(`Page ${i + 1}: page number "${no}"`);
    if (!p.querySelector(".pdf-head") || !p.querySelector(".pdf-foot")?.textContent?.includes("Generated")) problems.push(`Page ${i + 1}: header or footer missing`);
    const body = p.querySelector<HTMLElement>(".pdf-body")!;
    const b = body.getBoundingClientRect();
    if (body.scrollHeight > body.clientHeight + 1) problems.push(`Page ${i + 1}: content runs past the bottom margin`);
    for (const el of Array.from(body.querySelectorAll<HTMLElement>("*"))) {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (r.right > b.right + 2 || r.left < b.left - 2) { problems.push(`Page ${i + 1}: ${el.tagName.toLowerCase()}${el.className ? `.${String(el.className).split(" ")[0]}` : ""} goes past the side margin`); break; }
    }
    for (const k of Array.from(body.querySelectorAll<HTMLElement>("[data-block]"))) {
      const id = k.dataset.block!;
      if (seen.has(id) && seen.get(id) !== i) problems.push(`Figure ${id} is split across pages`);
      seen.set(id, i);
    }
    const lastChild = body.lastElementChild as HTMLElement | null;
    if (lastChild && isHeading(lastChild) && i < pages.length - 1) problems.push(`Page ${i + 1}: heading "${lastChild.textContent}" left alone at the bottom`);
  });
  return problems;
}
(window as any).__falconPdf = { layoutDocument, checkLayout, tidyText, pageCountOf: async (host: HTMLElement) => { const b = await pagesToPdfBytes(host); return (new TextDecoder("latin1").decode(b).match(/\/Type \/Page /g) || []).length; } };
